import { useEffect, useState } from 'react';
import { Download, FileText } from 'lucide-react';

import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { Modal } from '@/components/ui/Modal';
import { QueryError } from '@/components/ui/QueryError';
import { Skeleton } from '@/components/ui/Skeleton';
import { toast } from '@/hooks/useToast';
import { ApiRequestError } from '@/lib/apiClient';
import { fetchPrivateFile, saveBlob } from '@/lib/download';

/**
 * The types drawn in the dialog — images in an `<img>`, video in a `<video>`,
 * PDFs in the browser's own viewer.
 *
 * An allow-list on purpose. The file is shown from a `blob:` URL, which belongs
 * to our origin, and the server sends every upload as `attachment` precisely so
 * nothing is ever rendered as a page of ours. A raster image cannot run script,
 * a video only plays, and a PDF opens in the browser's sandboxed viewer; an SVG or an HTML file
 * would run as one of our pages, so they — and anything else — are offered as a
 * download instead.
 */
const PREVIEWABLE = new Set([
  'image/png',
  'image/jpeg',
  'image/webp',
  'application/pdf',
  'video/mp4',
  'video/webm',
  'video/quicktime',
]);

/** Whether the dialog can draw this type, or will offer a download instead. */
export function isPreviewable(type: string): boolean {
  return PREVIEWABLE.has(type);
}

/**
 * What to show: a stored file, fetched by its API path, or one chosen on this
 * device and not uploaded yet, read straight from memory. Null keeps the
 * dialog closed.
 */
export type PreviewSource = { path: string } | { file: Blob };

export interface FilePreviewModalProps {
  source: PreviewSource | null;
  /** What the file is called — the dialog's title and the saved file's name. */
  fileName: string;
  onClose: () => void;
  /**
   * Turns the dialog into a last look before sending: the footer offers this
   * action instead of Download. For a file chosen on this device and not
   * uploaded yet — "is this the right one?" before it reaches the teacher.
   */
  confirm?: { label: string; isLoading?: boolean; onConfirm: () => void };
}

type Loaded = { blob: Blob; url: string | null };

/**
 * A private file, opened where the person is rather than saved to their disk.
 *
 * Fetched with the access token like every other read (CLAUDE.md §11) — there is
 * no public link to point an `<iframe>` at — and the object URL is revoked when
 * the dialog closes, so the file does not outlive the look. Download saves the
 * blob already in hand rather than fetching it again.
 */
export function FilePreviewModal({ source, fileName, onClose, confirm }: FilePreviewModalProps) {
  const [loaded, setLoaded] = useState<Loaded | null>(null);
  const [error, setError] = useState<{ message: string } | null>(null);
  const [attempt, setAttempt] = useState(0);

  // Keyed on what the source points at, not the object: callers build
  // `{ path }` inline, and a fresh object every render would fetch forever.
  const localFile = source && 'file' in source ? source.file : null;
  const path = source && 'path' in source ? source.path : null;

  useEffect(() => {
    if (!localFile && !path) return;

    let cancelled = false;
    let objectUrl: string | null = null;
    setLoaded(null);
    setError(null);

    const read = localFile ? Promise.resolve(localFile) : fetchPrivateFile(path ?? '');
    read
      .then((blob) => {
        if (cancelled) return;
        objectUrl = PREVIEWABLE.has(blob.type) ? URL.createObjectURL(blob) : null;
        setLoaded({ blob, url: objectUrl });
      })
      .catch((caught: unknown) => {
        if (cancelled) return;
        setError({
          message: caught instanceof ApiRequestError ? caught.message : 'Please try again.',
        });
      });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [localFile, path, attempt]);

  function download() {
    if (!loaded) return;
    try {
      saveBlob(loaded.blob, fileName);
    } catch {
      toast.error(`Could not save ${fileName}`, 'Please try again.');
    }
  }

  const isPdf = loaded?.blob.type === 'application/pdf';
  const isVideo = loaded?.blob.type.startsWith('video/') ?? false;

  return (
    <Modal
      isOpen={Boolean(source)}
      onClose={onClose}
      title={fileName}
      size="xl"
      footer={
        confirm ? (
          <div className="flex justify-end gap-2.5">
            <Button variant="ghost" type="button" onClick={onClose} disabled={confirm.isLoading}>
              Cancel
            </Button>
            <Button
              variant="primary"
              type="button"
              isLoading={confirm.isLoading}
              onClick={confirm.onConfirm}
            >
              {confirm.label}
            </Button>
          </div>
        ) : (
          <div className="flex justify-end gap-2.5">
            <Button variant="ghost" type="button" onClick={onClose}>
              Close
            </Button>
            <Button
              variant="secondary"
              type="button"
              leftIcon={<Download className="h-4 w-4" />}
              disabled={!loaded}
              onClick={download}
            >
              Download
            </Button>
          </div>
        )
      }
    >
      {error ? (
        <QueryError
          title={`Could not open ${fileName}`}
          error={error}
          onRetry={() => setAttempt((count) => count + 1)}
        />
      ) : !loaded ? (
        <div role="status" aria-label={`Loading ${fileName}`}>
          <Skeleton className="h-[60vh] w-full rounded-xl" />
        </div>
      ) : !loaded.url ? (
        <EmptyState
          icon={<FileText className="h-5 w-5" />}
          title="This file cannot be shown here"
          description={
            confirm
              ? `Check that ${fileName} is the right file before you continue.`
              : 'Download it to open it on your device.'
          }
          action={
            confirm ? undefined : (
              <Button variant="secondary" leftIcon={<Download className="h-4 w-4" />} onClick={download}>
                Download
              </Button>
            )
          }
        />
      ) : isVideo ? (
        <video
          autoPlay
          playsInline
          src={loaded.url}
          controls
          className="max-h-[70vh] w-full rounded-xl border border-line bg-surface-2"
        >
          <track kind="captions" />
        </video>
      ) : isPdf ? (
        <iframe
          title={fileName}
          src={loaded.url}
          className="h-[70vh] w-full rounded-xl border border-line bg-surface-2"
        />
      ) : (
        <div className="grid place-items-center rounded-xl border border-line bg-surface-2 p-2">
          <img src={loaded.url} alt={fileName} className="max-h-[70vh] w-auto max-w-full object-contain" />
        </div>
      )}
    </Modal>
  );
}
